import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, Quote, Star, Play } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1];

function StarRow({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i < count ? 'text-[#E8C25A] fill-[#E8C25A]' : 'text-slatebody/30'}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function toEmbedUrl(url) {
  if (!url) return null;
  if (url.includes('/embed/')) return url;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (match) return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1`;
  return url;
}

function isYouTubeUrl(url) {
  return url && (url.includes('youtube.com') || url.includes('youtu.be'));
}

/**
 * TestimonialModal — text OR video feedback popup.
 *
 * If testimonial.video_url is set  → shows video player (YouTube iframe or <video>)
 *                                    with author info below
 * Otherwise                        → shows full text quote layout
 *
 * Props:
 *   testimonial: { quote, name, role, company, location, avatar, rating?, video_url? } | null
 *   onClose:     () => void
 */
export default function TestimonialModal({ testimonial, onClose }) {
  const reducedMotion = useReducedMotion();
  const modalRef = useRef(null);
  const isOpen = !!testimonial;

  const hasVideo = isOpen && !!testimonial.video_url;
  const embedUrl = hasVideo ? toEmbedUrl(testimonial.video_url) : null;
  const isYT = hasVideo && isYouTubeUrl(testimonial.video_url);

  useEffect(() => {
    if (!isOpen) return;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);

    setTimeout(() => {
      modalRef.current?.querySelector('[data-testid="testimonial-modal-close"]')?.focus();
    }, 50);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isOpen, onClose]);

  const subline = testimonial
    ? (() => {
        const roleCompany =
          testimonial.role && testimonial.company
            ? `${testimonial.role} of ${testimonial.company}`
            : testimonial.role || testimonial.company || '';
        return [roleCompany, testimonial.location].filter(Boolean).join(' · ');
      })()
    : '';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={`Feedback from ${testimonial?.name || 'client'}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.25 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-navy-deep/75 backdrop-blur-sm p-4 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
          data-testid="testimonial-modal-overlay"
        >
          <motion.div
            ref={modalRef}
            initial={{ scale: reducedMotion ? 1 : 0.92, y: reducedMotion ? 0 : 22, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: reducedMotion ? 1 : 0.96, opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.38, ease: EASE }}
            className={`relative w-full rounded-3xl bg-white shadow-[0_30px_80px_rgba(15,25,45,0.45)] ring-1 ring-black/5 overflow-hidden ${
              hasVideo ? 'max-w-3xl' : 'max-w-2xl'
            }`}
            onClick={(e) => e.stopPropagation()}
            data-testid="testimonial-modal"
          >
            {/* ── Close button ── */}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute top-4 right-4 z-20 h-10 w-10 inline-flex items-center justify-center rounded-full bg-white border border-hairline text-navy-deep hover:bg-sky-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E]/60"
              data-testid="testimonial-modal-close"
            >
              <X className="h-4 w-4" />
            </button>

            {/* ══ VIDEO MODE ══ */}
            {hasVideo && (
              <>
                {/* Video player */}
                <div
                  className="relative w-full bg-black"
                  style={{ paddingBottom: '56.25%' }}
                >
                  {/* Gold top accent */}
                  <div className="absolute top-0 left-0 right-0 h-[3px] z-10"
                    style={{ background: 'linear-gradient(90deg, #2F6FB0, #E8C25A, #C9962E)' }}
                  />

                  {isYT ? (
                    <iframe
                      src={embedUrl}
                      title={`Video feedback from ${testimonial.name}`}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={embedUrl}
                      controls
                      className="absolute inset-0 w-full h-full object-contain bg-black"
                    >
                      Your browser does not support the video tag.
                    </video>
                  )}

                  {/* Play icon overlay (decorative) */}
                  <div className="absolute bottom-4 left-5 z-10 flex items-center gap-2 pointer-events-none">
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full"
                      style={{ background: 'rgba(201,150,46,0.92)', color: '#08111F' }}
                    >
                      <Play className="h-3 w-3 fill-current" />
                      Video Feedback
                    </span>
                  </div>
                </div>

                {/* Author + quote strip below video */}
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-4">
                    {testimonial.avatar ? (
                      <img
                        src={testimonial.avatar}
                        alt=""
                        aria-hidden="true"
                        className="h-14 w-14 rounded-full object-cover ring-2 ring-white shadow-card flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="h-14 w-14 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #2F6FB0, #E8C25A)' }}
                      >
                        <Play className="h-5 w-5 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-base sm:text-lg font-bold text-navy-deep">
                        {testimonial.name}
                      </p>
                      {subline && (
                        <p className="text-sm text-slatebody mt-0.5">{subline}</p>
                      )}
                    </div>
                    {typeof testimonial.rating === 'number' && (
                      <StarRow count={testimonial.rating} />
                    )}
                  </div>

                  {testimonial.quote && (
                    <p className="mt-5 text-sm text-slatebody leading-relaxed italic border-l-2 border-[#E8C25A]/60 pl-4">
                      &ldquo;{testimonial.quote}&rdquo;
                    </p>
                  )}
                </div>
              </>
            )}

            {/* ══ TEXT MODE ══ */}
            {!hasVideo && (
              <div className="p-8 sm:p-12">
                {/* Gold top accent */}
                <div
                  className="absolute top-0 left-0 right-0 h-[3px]"
                  style={{ background: 'linear-gradient(90deg, #2F6FB0, #E8C25A, #C9962E)' }}
                />

                <Quote className="h-10 w-10 text-[#C9962E]" strokeWidth={1.6} />

                {typeof testimonial.rating === 'number' && (
                  <div className="mt-4">
                    <StarRow count={testimonial.rating} />
                  </div>
                )}

                <p
                  className="mt-6 text-xl sm:text-2xl font-medium text-navy-deep leading-relaxed"
                  data-testid="testimonial-modal-quote"
                >
                  &ldquo;{testimonial.quote}&rdquo;
                </p>

                <div className="mt-10 flex items-center gap-4">
                  {testimonial.avatar ? (
                    <img
                      src={testimonial.avatar}
                      alt=""
                      aria-hidden="true"
                      className="h-14 w-14 rounded-full object-cover ring-2 ring-white shadow-card"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-sky-100" aria-hidden="true" />
                  )}
                  <div>
                    <p
                      className="text-base sm:text-lg font-bold text-navy-deep"
                      data-testid="testimonial-modal-name"
                    >
                      {testimonial.name}
                    </p>
                    {subline && (
                      <p
                        className="text-sm text-slatebody mt-0.5"
                        data-testid="testimonial-modal-subline"
                      >
                        {subline}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
